import { useCallback, useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import Vezgo from "vezgo-sdk-js";
import { trpc } from "@utils";
import useAuth from "./useAuth";

// Types
interface VezgoAccountCreationError {
    error_type: number;
    message: string;
    account: string;
}

interface ConnectedAccountData {
    id: string;
    name: string;
    platform: string;
    connectionType: string;
}

interface VezgoClientData {
    client: Vezgo.APIInterface;
    teamId: string;
}

interface VezgoUserData {
    vezgoUser: Vezgo.APIUserInterface;
    teamId: string;
}

interface VezgoProvidersData {
    all: Vezgo.Provider[];
    cexList: Vezgo.Provider[];
    cexWithTransactions: Vezgo.Provider[];
}

// Sentry logging utilities
const logSuccessfulConnection = (
    teamId: string,
    accountId: string,
    providerName: string,
) => {
    Sentry.captureMessage("Vezgo account connected", {
        fingerprint: ["vezgo-account-successful-connection"],
        level: "info",
        extra: {
            accountId,
            teamId,
            provider: providerName,
        },
        tags: { teamId },
    });
};

const logWidgetEvent = (teamId: string, eventName: string, eventData: any) => {
    Sentry.captureMessage("Vezgo widget event", {
        level: "info",
        fingerprint: ["vezgo-events"],
        extra: {
            name: eventName,
            data: JSON.stringify(eventData),
            teamId,
        },
        tags: { teamId },
    });
};

const logConnectionClosedByUser = (
    teamId: string,
    error: VezgoAccountCreationError,
) => {
    Sentry.captureMessage("Vezgo connection closed by user", {
        fingerprint: ["vezgo-connection-closed"],
        level: "info",
        extra: {
            error: JSON.stringify(error),
            teamId,
        },
        tags: { teamId },
    });
};

const logAccountCreationError = (
    teamId: string,
    error: VezgoAccountCreationError,
) => {
    Sentry.captureMessage("Error while creating Vezgo account", {
        fingerprint: ["vezgo-account-creation-error"],
        level: "error",
        extra: {
            error: JSON.stringify(error),
            teamId,
        },
        tags: { teamId },
    });
};

/**
 * Creates and configures a Vezgo client instance
 */
const createVezgoClient = (
    organizationId: string | null,
    accessToken: string | null,
): Vezgo.APIInterface | null => {
    if (typeof window === "undefined" || !organizationId || !accessToken) {
        return null;
    }

    return Vezgo.init({
        clientId: process.env.NEXT_PUBLIC_VEZGO_CLIENT_ID!,
        authEndpoint: "/api/vezgo/auth",
        auth: {
            params: { organizationId, accessToken },
        },
    });
};

/**
 * Hook to get the Vezgo client and team information
 */
const useVezgoClient = (): VezgoClientData | null => {
    const { session, sessionLoading } = useAuth();
    const userAccessToken = session?.user?.accessToken;
    const teamOrganizationId = session?.stale_team?.organizationId;

    return useMemo(() => {
        if (!userAccessToken || !teamOrganizationId || sessionLoading) {
            return null;
        }

        const vezgoClient = createVezgoClient(
            teamOrganizationId,
            userAccessToken,
        );
        if (!vezgoClient) {
            return null;
        }

        return {
            client: vezgoClient,
            teamId: session?.teamId,
        };
    }, [teamOrganizationId, userAccessToken, sessionLoading, session?.teamId]);
};

/**
 * Hook to get the authenticated Vezgo user
 */
const useVezgoUser = (): VezgoUserData | null => {
    const clientData = useVezgoClient();

    if (!clientData?.client || !clientData.teamId) {
        return null;
    }

    return {
        vezgoUser: clientData.client.login(),
        teamId: clientData.teamId,
    };
};

/**
 * Transforms Vezgo account data to our internal format
 */
const transformAccountData = (
    vezgoAccount: Vezgo.Account,
): ConnectedAccountData => ({
    id: vezgoAccount.id || "",
    name:
        vezgoAccount.provider?.display_name ||
        vezgoAccount.provider?.name ||
        "Exchange",
    platform:
        vezgoAccount.provider?.display_name ||
        vezgoAccount.provider?.name ||
        "Unknown",
    connectionType: "API",
});

/**
 * Gets available provider names from the API response
 */
const extractProviderNames = (
    providersData: VezgoProvidersData | undefined,
): string[] => {
    if (!providersData?.cexWithTransactions) {
        return [];
    }

    return providersData.cexWithTransactions
        .map((provider) => provider.name)
        .filter(
            (name): name is string =>
                typeof name === "string" && name !== undefined,
        );
};

/**
 * Checks if the error is a user-initiated connection closure
 */
const isConnectionClosedByUser = (
    error: VezgoAccountCreationError,
): boolean => {
    return error?.error_type === 400 && error?.message === "Connection closed";
};

/**
 * Main hook for connecting Vezgo accounts
 */
export const useConnectAccount = () => {
    const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);

    const { data: providersData, isLoading: isProvidersLoading } =
        trpc.integrations.vezgo.getProviders.useQuery();

    const vezgoUserData = useVezgoUser();

    const availableProviders = useMemo(
        () => extractProviderNames(providersData),
        [providersData],
    );

    /**
     * Handles successful account connection
     */
    const handleConnectionSuccess = useCallback(
        async (
            accountId: string,
            vezgoUser: Vezgo.APIUserInterface,
            teamId: string,
            onConnect?: (account: ConnectedAccountData) => void,
        ) => {
            try {
                const connectedAccount: Vezgo.Account =
                    await vezgoUser.accounts.getOne(accountId);

                logSuccessfulConnection(
                    teamId,
                    accountId,
                    connectedAccount.provider?.name || "Unknown",
                );
                setIsConnectionModalOpen(false);

                if (onConnect) {
                    const transformedAccount =
                        transformAccountData(connectedAccount);
                    onConnect(transformedAccount);
                }
            } catch (error) {
                console.error("Error handling connection success:", error);
                setIsConnectionModalOpen(false);
            }
        },
        [],
    );

    /**
     * Handles connection errors with special case for user-initiated closures
     */
    const handleConnectionError = useCallback(
        (error: VezgoAccountCreationError, teamId: string) => {
            if (isConnectionClosedByUser(error)) {
                logConnectionClosedByUser(teamId, error);
            } else {
                logAccountCreationError(teamId, error);
            }
            setIsConnectionModalOpen(false);
        },
        [],
    );

    /**
     * Handles widget events
     */
    const handleWidgetEvent = useCallback(
        (eventName: string, eventData: any, teamId: string) => {
            logWidgetEvent(teamId, eventName, eventData);
        },
        [],
    );

    /**
     * Initiates the account connection flow
     */
    const initiateAccountConnection = useCallback(
        async (onConnect?: (account: ConnectedAccountData) => void) => {
            // Guard clauses for required data
            if (!providersData || isProvidersLoading || !vezgoUserData) {
                return;
            }

            const { vezgoUser, teamId } = vezgoUserData;
            setIsConnectionModalOpen(true);

            try {
                vezgoUser
                    .connect({
                        theme: "light",
                        syncNfts: true,
                        providers: availableProviders,
                    })
                    .onConnection(async (accountId: string) => {
                        await handleConnectionSuccess(
                            accountId,
                            vezgoUser,
                            teamId,
                            onConnect,
                        );
                    })
                    .onEvent(async (eventName: string, eventData: any) => {
                        handleWidgetEvent(eventName, eventData, teamId);
                    })
                    .onError(async (error: VezgoAccountCreationError) => {
                        handleConnectionError(error, teamId);
                    });
            } catch (error) {
                console.error("Error initiating account connection:", error);
                setIsConnectionModalOpen(false);
            }
        },
        [
            providersData,
            isProvidersLoading,
            vezgoUserData,
            availableProviders,
            handleConnectionSuccess,
            handleConnectionError,
            handleWidgetEvent,
        ],
    );

    return {
        handleConnectAccount: initiateAccountConnection,
        isLoading: isProvidersLoading,
        isOpen: isConnectionModalOpen,
    };
};
